import React, { FC, useCallback, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { Button, Collapse, Space, Table } from "antd"
import { ColumnsType } from "antd/lib/table"
import { notifications } from "notifications"
import { useStore } from "stores/utils/use-store"
import {
  AccountDailyMissionProgress,
  DailyMissionPostbackKind,
  FullDailyMissionKind,
} from "../store/postbacks.store"
import { GamePlatformType } from "../store/account-games-BuffPlay.store"

const { Panel } = Collapse

type Props = {
  accountId: string
  gameId: string
  platform: GamePlatformType
}

export const AccountDailyMissionsSection: FC<Props> = observer(({ accountId, gameId, platform }) => {
  const { dataStore: { accountPostbacksStore } } = useStore()
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const rows = accountPostbacksStore.getDailyMissions(gameId, platform)

  const refetch = useCallback(async() => {
    setLoading(true)
    try {
      await accountPostbacksStore.loadDailyMissions(accountId, gameId, platform)
    } catch (err: any) {
      notifications.error(err?.message || "Failed to load daily missions")
    } finally {
      setLoading(false)
    }
  }, [accountId, gameId, platform, accountPostbacksStore])

  useEffect(() => {
    void refetch()
  }, [refetch])

  const onTick = async(row: AccountDailyMissionProgress) => {
    if (row.kind === FullDailyMissionKind.LOGIN_STREAK) {
      notifications.info("LOGIN_STREAK is account-wide; use the button at the top of the account page.")

      return
    }
    setBusy(true)
    try {
      await accountPostbacksStore.tickDailyMission(
        accountId,
        gameId,
        row.kind as DailyMissionPostbackKind,
        platform,
      )
      notifications.success("Tick recorded")
      await refetch()
    } catch (err: any) {
      notifications.error(err?.message || "Failed to tick mission")
    } finally {
      setBusy(false)
    }
  }

  const onClaim = async(row: AccountDailyMissionProgress) => {
    setBusy(true)
    try {
      await accountPostbacksStore.claimDailyMission(accountId, row.id)
      notifications.success("Mission claimed")
      await refetch()
    } catch (err: any) {
      notifications.error(err?.message || "Failed to claim mission")
    } finally {
      setBusy(false)
    }
  }

  const columns: ColumnsType<AccountDailyMissionProgress> = [
    { title: "Kind", dataIndex: "kind", key: "kind", width: 140 },
    { title: "Title", dataIndex: "title", key: "title" },
    {
      title: "Progress",
      key: "progress",
      width: 110,
      render: (_, row) => `${row.currentCount} / ${row.requiredCount}`,
    },
    {
      title: "Threshold",
      dataIndex: "requiredCount",
      key: "threshold",
      width: 100,
    },
    {
      title: "Status",
      key: "status",
      width: 110,
      render: (_, row) => (row.isClaimed ? "Claimed" : row.isClaimable ? "Claimable" : "In progress"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_, row) => {
        const claimDisabled = busy || row.isClaimed || !row.isClaimable
        const tickDisabled = busy || row.isClaimed || row.kind === FullDailyMissionKind.LOGIN_STREAK
        const tickTitle = row.kind === FullDailyMissionKind.LOGIN_STREAK
          ? "LOGIN_STREAK is account-wide — use the button on the account page"
          : undefined

        return (
          <Space>
            <Button size="small" disabled={tickDisabled} onClick={() => onTick(row)} title={tickTitle}>
              Tick +1
            </Button>
            <Button size="small" type="primary" disabled={claimDisabled} onClick={() => onClaim(row)}>
              Claim
            </Button>
          </Space>
        )
      },
    },
  ]

  return (
    <Collapse style={{ marginTop: 20 }} defaultActiveKey={["daily-missions"]}>
      <Panel key="daily-missions" header={`Daily Missions (${platform}) — ${rows.length}`}>
        <Table<AccountDailyMissionProgress>
          dataSource={rows}
          columns={columns}
          rowKey="id"
          pagination={false}
          loading={loading}
          bordered
          size="small"
        />
      </Panel>
    </Collapse>
  )
})
